/*
 * Copyright (C) 2024 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {htmlEscape} from '@instructure/html-escape'
import type {RubricAssessment} from '@canvas/grading/grading'
import type {
  Rubric,
  RubricAssessmentData,
  RubricAssociation,
  RubricRating,
} from '@canvas/rubrics/react/types/rubric'

export type RubricUnderscoreType = {
  title: string
  button_display: string
  criteria: RubricUnderscoreCriteria[]
  data?: RubricUnderscoreCriteria[]
  hide_points: boolean
  id: string
  rating_order: string
  free_form_criterion_comments: boolean
  points_possible: number
  public: boolean
  unassessed?: boolean
  workflow_state: string
  can_update?: boolean
}

type RubricUnderscoreCriteria = {
  criterion_use_range: boolean
  description: string
  id: string
  learning_outcome_id?: string
  long_description: string
  ignore_for_scoring?: boolean
  mastery_points?: number
  points: number
  generated?: boolean
  ratings: {
    criterion_id: string
    description: string
    id: string
    long_description: string
    points: number
  }[]
}

export type RubricOutcomeUnderscore = {
  id: string
  display_name: string
}

export type RubricAssessmentUnderscore = RubricAssessment & {
  data: RubricAssessmentDataUnderscore[]
}

export type RubricAssessmentDataUnderscore = {
  id: string
  points: number
  criterion_id: string
  learning_outcome_id?: string
  comments: string
  comments_enabled: boolean
  description: string
}
export const mapRubricUnderscoredKeysToCamelCase = (
  rubric: RubricUnderscoreType,
  rubricOutcomeData: RubricOutcomeUnderscore[] = [],
): Rubric => {
  const rubricOutcomeMap = rubricOutcomeData.reduce(
    (prev, curr) => {
      prev[curr.id] = curr.display_name
      return prev
    },
    {} as Record<string, string>,
  )

  const criteria = rubric.criteria ?? rubric.data ?? []

  return {
    title: rubric.title,
    criteria: criteria.map(criterion => {
      const {learning_outcome_id} = criterion

      return {
        criterionUseRange: criterion.criterion_use_range,
        description: criterion.description,
        id: criterion.id,
        longDescription: criterion.long_description,
        learningOutcomeId: criterion.learning_outcome_id,
        ignoreForScoring: criterion.ignore_for_scoring,
        points: criterion.points,
        masteryPoints: criterion.mastery_points,
        isGenerated: criterion.generated,
        outcome: learning_outcome_id
          ? {
              displayName: rubricOutcomeMap[learning_outcome_id],
              title: criterion.description,
            }
          : undefined,
        ratings: criterion.ratings.map(rating => {
          return {
            criterionId: rating.criterion_id,
            description: rating.description,
            id: rating.id,
            longDescription: rating.long_description,
            points: rating.points,
          }
        }),
      }
    }),
    ratingOrder: rubric.rating_order,
    freeFormCriterionComments: rubric.free_form_criterion_comments,
    pointsPossible: rubric.points_possible,
    public: rubric.public,
    criteriaCount: criteria.length,
    hidePoints: rubric.hide_points,
    id: rubric.id,
    buttonDisplay: rubric.button_display,
    unassessed: rubric.unassessed,
    workflowState: rubric.workflow_state,
    canUpdateRubric: rubric.can_update,
  }
}

/**
 * Decodes all HTML entities (including &#39; from Ruby's html_escape)
 * using the browser's own HTML parser. More complete than front end package htmlEscape
 * unescape, which only handles entities it produces itself (e.g. &#x27; not &#39;).
 */
export const decodeHTML = (str: string): string => {
  const el = document.createElement('textarea')
  el.innerHTML = str
  return el.value
}

/**
 * Prepares a long description string for safe use in dangerouslySetInnerHTML.
 * - Decodes all HTML entities from the backend (handles both &#39; and &#x27;)
 * - Strips legacy <br/> tags (backend sanitization artifact)
 * - Re-escapes for XSS safety
 * - Converts \n to <br /> for proper line break rendering
 */
export const formatLongDescriptionHTML = (str: string): string => {
  // Normalize <br/> to \n before decoding so behavior is consistent
  // across real browsers and jsdom (textarea.value handles \n reliably
  // but does not guarantee <br> → \n conversion)
  const decoded = decodeHTML(str.replace(/<br\s*\/?>/gi, '\n'))
  return htmlEscape(decoded).replace(/\n/g, '<br />')
}

export const mapRubricAssessmentDataUnderscoredKeysToCamelCase = (
  data: RubricAssessmentDataUnderscore[],
): RubricAssessmentData[] => {
  if (!data) return []

  return data.map(assessment => {
    return {
      id: assessment.id,
      points: assessment.points,
      criterionId: assessment.criterion_id,
      learningOutcomeId: assessment.learning_outcome_id,
      comments: assessment.comments,
      commentsEnabled: assessment.comments_enabled,
      description: assessment.description,
    }
  })
}

export type RubricAssociationUnderscore = {
  association_type: 'Assignment' | 'Account' | 'Course'
  association_id: string
  id: string
  rubric_id: string
  use_for_grading: boolean
  hide_points: boolean
  hide_score_total: boolean
  hide_outcome_results: boolean
  can_update?: boolean
  can_delete?: boolean
}
export const mapRubricAssociationUnderscoredKeysToCamelCase = (
  underscoreAssociation: RubricAssociationUnderscore,
): RubricAssociation => {
  return {
    associationType: underscoreAssociation.association_type,
    associationId: underscoreAssociation.association_id,
    id: underscoreAssociation.id,
    hideOutcomeResults: underscoreAssociation.hide_outcome_results,
    hidePoints: underscoreAssociation.hide_points,
    hideScoreTotal: underscoreAssociation.hide_score_total,
    useForGrading: underscoreAssociation.use_for_grading,
    canUpdate: underscoreAssociation.can_update,
    canDelete: underscoreAssociation.can_delete,
  }
}

type ReorderProps = {
  list: RubricRating[]
  startIndex: number
  endIndex: number
}

export const reorderRatingsAtIndex = ({list, startIndex, endIndex}: ReorderProps) => {
  const result = Array.from(list)
  const resultCopy = JSON.parse(JSON.stringify(list))

  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  result.forEach((item, index) => {
    item.points = resultCopy[index].points
  })

  return result
}
